import { useTranslation } from 'react-i18next';
import ServiceLayout from './components/ServiceLayout';
import useSeoPage from '@/hooks/useSeoPage';

export default function TherapeuticPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  useSeoPage({
    title: 'Терапевтическая стоматология в Кишинёве | Лечение зубов | Zimbet32',
    description: 'Лечение кариеса, пульпита и периодонтита в Кишинёве. Терапевтическая стоматология Zimbet32 — stomatologie terapeutică modernă în Chișinău cu tratament fără durere.',
    keywords: 'терапевтическая стоматология Кишинёв, лечение кариеса, лечение пульпита, реставрация зубов, stomatologie terapeutică Chișinău, Zimbet32',
    canonicalPath: '/services/therapeutic',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'MedicalProcedure',
      name: 'Терапевтическая стоматология',
      url: `${siteUrl}/services/therapeutic`,
      description: 'Лечение кариеса, пульпита, периодонтита с использованием современных материалов и технологий в Кишинёве.',
      procedureType: 'https://schema.org/TherapeuticProcedure',
      bodyLocation: 'Зубы',
      provider: {
        '@type': 'Dentist',
        name: 'Zimbet32',
        url: siteUrl,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Strada București 60',
          addressLocality: 'Chișinău',
          postalCode: 'MD-2012',
          addressCountry: 'MD',
        },
        telephone: '+373-22-22-07-09',
      },
    },
  });

  const procedures = [
    {
      icon: 'ri-tooth-line',
      title: t('servicePages.therapeutic.procedure1'),
      description: t('servicePages.therapeutic.procedure1Desc')
    },
    {
      icon: 'ri-heart-pulse-line',
      title: t('servicePages.therapeutic.procedure2'),
      description: t('servicePages.therapeutic.procedure2Desc')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('servicePages.therapeutic.procedure3'),
      description: t('servicePages.therapeutic.procedure3Desc')
    },
    {
      icon: 'ri-microscope-line',
      title: t('servicePages.therapeutic.procedure4'),
      description: t('servicePages.therapeutic.procedure4Desc')
    },
    {
      icon: 'ri-stethoscope-line',
      title: t('servicePages.therapeutic.procedure5'),
      description: t('servicePages.therapeutic.procedure5Desc')
    },
    {
      icon: 'ri-first-aid-kit-line',
      title: t('servicePages.therapeutic.procedure6'),
      description: t('servicePages.therapeutic.procedure6Desc')
    }
  ];

  const advantages = [
    {
      icon: 'ri-user-star-line',
      title: t('servicePages.therapeutic.benefit1'),
      description: t('servicePages.therapeutic.benefit1Desc')
    },
    {
      icon: 'ri-tools-line',
      title: t('servicePages.therapeutic.benefit2'),
      description: t('servicePages.therapeutic.benefit2Desc')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('servicePages.therapeutic.benefit3'),
      description: t('servicePages.therapeutic.benefit3Desc')
    }
  ];

  const galleryImages = [
    'https://public.readdy.ai/ai/img_res/a2adb28974863abb591034a1d63bd3b6.jpg',
    'https://readdy.ai/api/search-image?query=Close-up%20of%20dental%20caries%20treatment%20procedure%2C%20dentist%20hands%20working%20with%20professional%20tools%2C%20patient%20in%20dental%20chair%2C%20modern%20equipment%2C%20precise%20work%2C%20medical%20gloves%2C%20sterile%20instruments%2C%20professional%20dental%20care%2C%20clean%20clinical%20setting&width=800&height=600&seq=therapeutic-gallery-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Dental%20pulpitis%20treatment%20endodontic%20procedure%2C%20root%20canal%20therapy%2C%20professional%20dentist%20using%20microscope%2C%20precision%20instruments%2C%20modern%20dental%20technology%2C%20sterile%20environment%2C%20detailed%20medical%20work%2C%20professional%20healthcare%20setting&width=800&height=600&seq=therapeutic-gallery-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Professional%20teeth%20whitening%20procedure%20result%2C%20before%20and%20after%20comparison%2C%20bright%20healthy%20smile%2C%20dental%20aesthetics%2C%20modern%20cosmetic%20dentistry%2C%20patient%20satisfaction%2C%20clean%20clinical%20background%2C%20professional%20dental%20care%20outcome&width=800&height=600&seq=therapeutic-gallery-4&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Dental%20restoration%20work%20with%20composite%20materials%2C%20tooth%20filling%20procedure%2C%20dentist%20hands%20with%20instruments%2C%20precision%20work%2C%20modern%20dental%20materials%2C%20professional%20technique%2C%20sterile%20clinical%20environment%2C%20quality%20dental%20care&width=800&height=600&seq=therapeutic-gallery-5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Happy%20patient%20after%20successful%20dental%20treatment%2C%20satisfied%20smile%2C%20modern%20dental%20clinic%20interior%2C%20professional%20dentist%20in%20background%2C%20positive%20healthcare%20experience%2C%20comfortable%20atmosphere%2C%20quality%20dental%20service%20result&width=800&height=600&seq=therapeutic-gallery-6&orientation=landscape'
  ];

  return (
    <ServiceLayout
      heroImage="https://readdy.ai/api/search-image?query=Professional%20dental%20therapeutic%20treatment%20scene%2C%20modern%20dental%20clinic%20interior%2C%20dentist%20examining%20patient%20teeth%2C%20advanced%20medical%20equipment%2C%20bright%20clean%20environment%2C%20professional%20healthcare%20setting%2C%20comfortable%20patient%20care%2C%20contemporary%20dental%20office%20design&width=1920&height=600&seq=therapeutic-hero&orientation=landscape"
      breadcrumbKey="servicePages.therapeutic.title"
      titleKey="servicePages.therapeutic.title"
      descriptionKey="servicePages.therapeutic.description"
      procedures={procedures}
      advantages={advantages}
      galleryImages={galleryImages}
      ctaTitleKey="servicePages.therapeutic.ctaTitle"
      ctaDescriptionKey="servicePages.therapeutic.ctaDescription"
      ctaButtonKey="servicePages.therapeutic.ctaButton"
    />
  );
}